// =============================================================================
// RoutinesTab — Combat Command Center
//
// Layout (top to bottom):
//   1. Inline toast notifications
//   2. ⚡ Campaign Modifiers — quick toggles + Add Modifier (all users can add)
//   3. 🎯 Active Execution — executor shown inline, rest of page stays visible
//   4. ⚔️ My Routines — prominent Roll button, secondary actions in icon row
//   5. 📚 World Library — search + import
//   6. ⏱️  Roll History — collapsible log of recent executions with hit/damage details
//   7. 🔌 VTT Integrations — TaleSpire Symbiote and browser-extension install guides
//
// Design rules:
//   • No browser alert() or confirm() — all feedback via inline toasts/banners
//   • Click-to-expand pattern for cards (consistent with existing BoardPage)
//   • Hover highlights on every interactive card
//   • Roll is the primary CTA — always left-most, largest
// =============================================================================
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, Swords, Pencil, Trash2, Download, Upload, Loader2,
  Play, BookOpen, Search, Library, Share2, X, Zap, Clock, Copy,
  ChevronDown, ChevronUp, Puzzle, GripVertical, Sword, Dices, Hash,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAppSelector } from '@/store/hooks';
import type {
  AttackRoutine, Campaign, LibraryRoutine, CombatModifierWithStates, RoutineNode, RollLog, World,
  CombatContextResponse,
} from '../../../../backend/shared/types/index';
import type { RuleSystem } from '../../../../backend/shared/types/index';
import RoutineBuilder from './RoutineBuilder';
import RoutineExecutor from './RoutineExecutor';
import ModifierPanel from './ModifierPanel';
import { useToast } from '@/hooks/useToast';
import {
  readRecentCombatContext, rememberedCharacterForScope, saveRecentCombatContext,
} from '@/utils/combatContext';
import {
  downloadTaleSpirePackage, TALESPIRE_SYMBIOTE_DOCS_URL, TALESPIRE_SYMBIOTE_FOLDER,
} from '@/lib/talespireSymbiote';

const CHROME_WEB_STORE_URL = 'https://chromewebstore.google.com/detail/darkfeld-combat-automation/abehfnpbifblhgkceghgkepiajbjnjbj';

interface Props {
  characterId:      string;
  charName:         string;
  gameSystem?:      RuleSystem;
  char?:            import('../../../../backend/shared/types/index').Character | null;
  campaignId?:      string;
  worldId?:         string;
  /** When true, suppresses the character pill-switcher (plugin context provides its own). */
  hideCharSwitcher?: boolean;
}

type BuildMode = 'create' | { edit: AttackRoutine } | { editLib: LibraryRoutine };

const SYS: Record<string, string> = { pathfinder1: 'PF1', dnd35: '3.5', dnd5e: '5e', custom: '?' };

// Session cache for active modifiers (5-minute TTL)
const MODIFIER_CACHE_KEY = (campaignId: string) => `darkfeld_modifiers_${campaignId}`;
const MODIFIER_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// ---------------------------------------------------------------------------
// Output targets — persisted per-character in localStorage (Phase 1).
// Phase 2 will move this to the character record for cross-device persistence.
// ---------------------------------------------------------------------------
/** Destinations to which roll results can be dispatched after execution.
 *  'dndbeyond' / 'roll20' — write pending roll; plugin routes to that VTT tab.
 *  'talespire'            — write pending roll; the TaleSpire Symbiote publishes it.
 *  'discord'              — no pending VTT roll; server posts directly to
 *                           campaign.combatChannelId. Use when no VTT tab is open.
 *  An empty set means copy-paste only.
 */
type OutputTarget = 'dndbeyond' | 'roll20' | 'talespire' | 'discord';

const OUTPUT_TARGET_KEY = (charId: string) => `darkfeld_output_target_${charId}`;

function getStoredOutputTargets(charId: string): OutputTarget[] {
  try {
    const v = localStorage.getItem(OUTPUT_TARGET_KEY(charId));
    if (!v || v === 'none') return [];
    if (v.startsWith('[')) {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed)
        ? parsed.filter((target): target is OutputTarget => ['roll20', 'dndbeyond', 'talespire', 'discord'].includes(target))
        : [];
    }
    // Migrate the original single-target preference without changing behavior.
    if (['roll20', 'dndbeyond', 'talespire', 'discord'].includes(v)) return [v as OutputTarget];
    // No stored value — default to copy-only so the plugin is opt-in
    return [];
  } catch { return []; }
}

function setStoredOutputTargets(charId: string, targets: OutputTarget[]): void {
  try { localStorage.setItem(OUTPUT_TARGET_KEY(charId), JSON.stringify(targets)); } catch { /* ignore */ }
}

function getCachedModifiers(campaignId: string): CombatModifierWithStates[] | null {
  try {
    const cached = sessionStorage.getItem(MODIFIER_CACHE_KEY(campaignId));
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > MODIFIER_CACHE_TTL) {
      sessionStorage.removeItem(MODIFIER_CACHE_KEY(campaignId));
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setCachedModifiers(campaignId: string, modifiers: CombatModifierWithStates[]): void {
  try {
    sessionStorage.setItem(MODIFIER_CACHE_KEY(campaignId), JSON.stringify({
      data: modifiers,
      timestamp: Date.now(),
    }));
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
}


// ---------------------------------------------------------------------------
// ToastList
// ---------------------------------------------------------------------------
function ToastList({ toasts, onDismiss }: {
  toasts: ReturnType<typeof useToast>['toasts'];
  onDismiss: (id: string) => void;
}) {
  if (!toasts.length) return null;
  return (
    <div className="space-y-1.5 mb-2">
      {toasts.map((t) => (
        <div key={t.id} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-xs ${
          t.ok ? 'bg-success/10 text-success border border-success/20'
               : 'bg-danger/10 text-danger border border-danger/20'
        }`}>
          <span>{t.message}</span>
          <button onClick={() => onDismiss(t.id)} className="shrink-0 opacity-60 hover:opacity-100">
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Library routine card
// ---------------------------------------------------------------------------
function LibraryCard({ r, isDM, onImport, onEdit, onDelete, importing }: {
  r:          LibraryRoutine;
  isDM:       boolean;
  onImport:   () => void;
  onEdit:     () => void;
  onDelete:   () => void;
  importing:  boolean;
}) {
  const [confirmDel, setConfirmDel] = useState(false);
  return (
    <div className="bg-darkbg-card border border-darkbg-border rounded-lg px-4 py-3
                    hover:border-gold/30 hover:bg-darkbg-card/80 transition-all duration-150 group">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0 space-y-0.5">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display text-sm text-parchment">{r.name}</p>
            <span className="text-[10px] text-parchment-muted/40 bg-darkbg-tertiary px-1.5 py-0.5 rounded shrink-0">
              {SYS[r.gameSystem] ?? r.gameSystem}
            </span>
            <span className="text-[10px] text-parchment-muted/30 shrink-0">
              {r.definition?.length ?? 0} attacks
            </span>
          </div>
          {r.description && <p className="text-xs text-parchment-muted/50 line-clamp-2">{r.description}</p>}
          {r.tags?.length ? (
            <div className="flex flex-wrap gap-1 pt-0.5">
              {r.tags.map((t) => (
                <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-darkbg-tertiary text-parchment-muted/40">{t}</span>
              ))}
            </div>
          ) : null}
          <p className="text-[10px] text-parchment-muted/30">Added by {r.createdByName ?? 'DM'}</p>
        </div>

        {!confirmDel ? (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={onImport} disabled={importing}
              className="flex items-center gap-1 px-2.5 py-1 text-xs border border-darkbg-border rounded
                         text-parchment-muted/60 hover:border-gold/40 hover:text-parchment transition-colors disabled:opacity-40">
              {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              Import
            </button>
            {isDM && (
              <>
                <button onClick={onEdit} title="Edit"
                  className="p-1.5 text-parchment-muted/40 hover:text-gold hover:bg-gold/10 rounded transition-colors">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setConfirmDel(true)} title="Delete from library"
                  className="p-1.5 text-parchment-muted/40 hover:text-danger hover:bg-danger/10 rounded transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 shrink-0 border-l border-danger/30 pl-3">
            <span className="text-xs text-parchment-muted whitespace-nowrap">Remove?</span>
            <button onClick={() => setConfirmDel(false)}
              className="px-2 py-0.5 text-xs text-parchment-muted border border-darkbg-border rounded hover:border-parchment-muted/50 transition-colors">
              No
            </button>
            <button onClick={onDelete}
              className="px-2 py-0.5 text-xs text-white bg-danger/80 hover:bg-danger rounded transition-colors">
              Yes
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Roll history card
// ---------------------------------------------------------------------------
function RollHistoryCard({ log }: { log: RollLog }) {
  const [expanded, setExpanded] = useState(false);
  const result = log.result;
  const createdDate = new Date(log.createdAt);
  const timeStr = createdDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const dateStr = createdDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="bg-darkbg-tertiary/40 border border-darkbg-border rounded px-3 py-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start gap-2 text-left hover:opacity-90 transition-opacity"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-xs text-parchment">{log.routineName}</span>
            <span className="text-[9px] text-parchment-muted/40">
              {result.hits}/{result.totalAttacks} hits
            </span>
            <span className="text-[9px] text-parchment-muted/30">
              {result.totalDamage} dmg
            </span>
          </div>
          <p className="text-[10px] text-parchment-muted/40">
            {dateStr} at {timeStr}
          </p>
        </div>
        <div className="shrink-0 text-parchment-muted/40">
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </div>
      </button>

      {expanded && (
        <div className="mt-2 pt-2 border-t border-darkbg-border/40 space-y-1 text-xs">
          {result.resolvedAttacks.map((atk, i) => (
            <div key={i} className="text-parchment-muted/60">
              <span className="font-mono text-[9px] text-parchment-muted/40 mr-1">
                {atk.isHit ? (atk.isCrit ? '⚡' : '✅') : '❌'}
              </span>
              <span>{atk.label}</span>
              <span className="text-parchment-muted/30 ml-1">
                {atk.d20} {atk.attackMod >= 0 ? '+' : ''}{atk.attackMod} = {atk.total} vs {atk.vsTarget}
              </span>
              {atk.isHit && atk.damage.length > 0 && (
                <div className="ml-4 text-parchment-muted/50 text-[9px]">
                  Dmg: {atk.damage.map((d) => d.diceExpr).join(' + ')} = {atk.attackDamage}
                </div>
              )}
            </div>
          ))}
          {result.conditionalDamageResults.length > 0 && (
            <div className="mt-1 pt-1 border-t border-darkbg-border/40 text-parchment-muted/50">
              {result.conditionalDamageResults.map((cd) => (
                <div key={cd.nodeId} className="text-[9px]">
                  {cd.triggered ? '⚡' : '○'} {cd.label}: {cd.total}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function RoutinesTab({ characterId, charName, gameSystem, char, campaignId, worldId, hideCharSwitcher }: Props) {
  const { user, viewAsRole, viewAsUser } = useAppSelector((s) => s.auth);
  const effectiveRole = viewAsUser?.role ?? viewAsRole ?? user?.role;
  const effectiveUserId = viewAsUser?.userId ?? (viewAsRole ? `preview-role-${viewAsRole}` : user?.userId);
  const isPreview = !!(viewAsUser || viewAsRole);
  const isAdmin = effectiveRole === 'admin';

  const { toasts, push, dismiss } = useToast();

  // ── My Routines state ─────────────────────────────────────────────────────
  const [routines,   setRoutines]   = useState<AttackRoutine[]>([]);
  const [modifiers,  setModifiersState]  = useState<CombatModifierWithStates[]>([]);
  const [campaignCharacters, setCampaignCharacters] = useState<{ characterId: string; name: string }[]>([]);
  const [combatContext, setCombatContext] = useState<CombatContextResponse | null>(null);

  // Wrapper to update modifiers AND cache in sync (avoids stale cache issue)
  const setModifiers = (newModifiers: CombatModifierWithStates[] | ((prev: CombatModifierWithStates[]) => CombatModifierWithStates[])) => {
    setModifiersState((prev) => {
      const updated = typeof newModifiers === 'function' ? newModifiers(prev) : newModifiers;
      // Update cache immediately whenever modifiers change
      if (campaignId) {
        setCachedModifiers(campaignId, updated);
      }
      return updated;
    });
  };
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);
  const [canManageCombatScope, setCanManageCombatScope] = useState(false);
  /** undefined = not yet loaded; null = no channel set; string = channel ID configured. */
  const [combatChannelId, setCombatChannelId] = useState<string | null | undefined>(undefined);

  /** All characters owned by the current user — used for the quick-switch pill row outside campaign context. */
  const [myChars, setMyChars] = useState<Array<{ characterId: string; name: string }>>([]);

  const navigate = useNavigate();
  const [buildMode,  setBuildMode]  = useState<BuildMode | null>(null);
  const [saving,     setSaving]     = useState(false);
  const [deleteId,   setDeleteId]   = useState<string | null>(null);
  const [deleting,   setDeleting]   = useState<string | null>(null);
  const [sharing,    setSharing]    = useState<string | null>(null);
  const [duplicating,setDuplicating]= useState<string | null>(null);
  const [modOpen,    setModOpen]    = useState(false);  // modifiers section collapsed by default

  // ── Active execution (inline — doesn't replace tab content) ──────────────
  const [executing,  setExecuting]  = useState<AttackRoutine | null>(null);
  const executorRef = useRef<HTMLDivElement>(null);

  // ── World Library ─────────────────────────────────────────────────────────
  const [library,       setLibrary]       = useState<LibraryRoutine[]>([]);
  const [libLoading,    setLibLoading]    = useState(false);
  const [libSearch,     setLibSearch]     = useState('');
  const [importingLibId,setImportingLibId]= useState<string | null>(null);
  const [libSaving,     setLibSaving]     = useState(false);
  const [libOpen,       setLibOpen]       = useState(false);

  // ── Roll History ──────────────────────────────────────────────────────────
  const [historyOpen,   setHistoryOpen]   = useState(false);
  const [historyLogs,   setHistoryLogs]   = useState<any[]>([]);
  const [historyLoading,setHistoryLoading]= useState(false);
  const [historyCursor, setHistoryCursor] = useState<string | null>(null);
  const [historyMore,   setHistoryMore]   = useState(false);

  // ── Browser Extension ─────────────────────────────────────────────────────
  const [pluginOpen,    setPluginOpen]    = useState(false); // VTT integration guides

  // ── Output target (where roll results are sent) ────────────────────────────
  const [outputTargets, setOutputTargetsState] = useState<OutputTarget[]>(
    () => isPreview ? [] : getStoredOutputTargets(characterId),
  );

  function toggleOutputTarget(target: OutputTarget) {
    const next = outputTargets.includes(target)
      ? outputTargets.filter((candidate) => candidate !== target)
      : [...outputTargets, target];
    setOutputTargetsState(next);
    if (isPreview) return;
    setStoredOutputTargets(characterId, next);
  }

  useEffect(() => {
    setOutputTargetsState(isPreview ? [] : getStoredOutputTargets(characterId));
  }, [characterId, isPreview]);

  useEffect(() => {
    if (!isPreview) saveRecentCombatContext(effectiveUserId, characterId, campaignId);
  }, [campaignId, characterId, effectiveUserId, isPreview]);

  useEffect(() => {
    apiFetch<CombatContextResponse>('/combat/context')
      .then(setCombatContext)
      .catch(() => setCombatContext(null));
  }, [effectiveUserId]);

  // ── Drag-to-reorder state ─────────────────────────────────────────────────
  const [dragIndex,     setDragIndex]     = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  function handleDragStart(i: number) { setDragIndex(i); }
  function handleDragOver(e: React.DragEvent, i: number) {
    e.preventDefault();
    if (i !== dragOverIndex) setDragOverIndex(i);
  }
  function handleDragLeave() { setDragOverIndex(null); }
  function handleDrop(e: React.DragEvent, i: number) {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) { setDragIndex(null); setDragOverIndex(null); return; }
    const reordered = [...routines];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(i, 0, moved);
    setRoutines(reordered);
    setDragIndex(null);
    setDragOverIndex(null);
    saveRoutineOrder(reordered.map((r) => r.routineId));
  }
  function handleDragEnd() { setDragIndex(null); setDragOverIndex(null); }

  async function saveRoutineOrder(order: string[]) {
    try {
      await apiFetch(`/characters/${characterId}/combat/routines/reorder`, {
        method: 'PATCH',
        body: JSON.stringify({ order }),
      });
    } catch (e: any) {
      push(e.message ?? 'Failed to save routine order', false);
    }
  }

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const loads: Promise<void>[] = [
      apiFetch<{ routines: AttackRoutine[] }>(`/characters/${characterId}/combat/routines`)
        .then((r) => setRoutines(r.routines))
        .catch((e) => setError(e.message ?? 'Failed to load routines')),
    ];
    if (campaignId) {
      setCampaignCharacters([]);
      // Try to load from session cache first
      const cached = getCachedModifiers(campaignId);
      if (cached) {
        setModifiers(cached);
      } else {
        loads.push(
          apiFetch<{ modifiers: CombatModifierWithStates[] }>(`/campaigns/${campaignId}/combat/modifiers`)
            .then((r) => {
              setModifiers(r.modifiers);
              setCachedModifiers(campaignId, r.modifiers);
            })
            .catch(() => {}),
        );
      }
      loads.push(
        apiFetch<{ characters: { characterId: string; name: string }[] }>(`/campaigns/${campaignId}/characters`)
          .then((r) => setCampaignCharacters(r.characters))
          .catch(() => {}),
      );
      // Fetch campaign to check Discord combat channel configuration and whether
      // the effective DM owns this campaign.
      loads.push(
        apiFetch<{ campaign: Pick<Campaign, 'combatChannelId' | 'ownerUserId'> }>(`/campaigns/${campaignId}`)
          .then((r) => {
            setCombatChannelId(r.campaign.combatChannelId ?? null);
            setCanManageCombatScope(isAdmin || (effectiveRole === 'dm' && r.campaign.ownerUserId === effectiveUserId));
          })
          .catch(() => {
            setCombatChannelId(null);
            setCanManageCombatScope(isAdmin);
          }),
      );
    } else if (worldId) {
      loads.push(
        apiFetch<{ world: Pick<World, 'ownerUserId'> }>(`/worlds/${worldId}`)
          .then((r) => setCanManageCombatScope(isAdmin || (effectiveRole === 'dm' && r.world.ownerUserId === effectiveUserId)))
          .catch(() => setCanManageCombatScope(isAdmin)),
      );
    } else {
      setCanManageCombatScope(isAdmin);
    }
    Promise.allSettled(loads).finally(() => setLoading(false));
  }, [characterId, campaignId, worldId, effectiveRole, effectiveUserId, isAdmin]);

  // Fetch the current user's own characters for the quick-switch pill row when
  // the character is not assigned to a campaign. Campaign-assigned characters
  // use the campaign roster loaded above so the switcher stays scoped to the
  // current campaign.
  useEffect(() => {
    if (campaignId) {
      setMyChars([]);
      return;
    }
    apiFetch<{ items: Array<{ characterId: string; name: string }> }>('/characters')
      .then((r) => setMyChars(r.items.map((c) => ({ characterId: c.characterId, name: c.name }))))
      .catch(() => {}); // non-fatal — switcher simply stays hidden
  }, [campaignId]);

  useEffect(() => {
    if (!worldId) return;
    setLibLoading(true);
    apiFetch<{ routines: LibraryRoutine[] }>(`/worlds/${worldId}/combat/routines`)
      .then((r) => setLibrary(r.routines))
      .catch(() => {})
      .finally(() => setLibLoading(false));
  }, [worldId]);

  // Scroll executor into view when it opens
  useEffect(() => {
    if (executing) executorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [executing]);

  // Load roll history when section opens
  useEffect(() => {
    if (historyOpen && historyLogs.length === 0 && !historyLoading) {
      loadHistory();
    }
  }, [historyOpen]);

  // ── My Routine CRUD ───────────────────────────────────────────────────────
  async function handleSave(data: { name: string; gameSystem: RuleSystem; definition: RoutineNode[] }) {
    setSaving(true);
    try {
      if (buildMode && typeof buildMode === 'object' && 'edit' in buildMode) {
        const res = await apiFetch<{ routine: AttackRoutine }>(
          `/characters/${characterId}/combat/routines/${buildMode.edit.routineId}`,
          { method: 'PATCH', body: JSON.stringify(data) },
        );
        setRoutines((p) => p.map((r) => r.routineId === buildMode.edit.routineId ? res.routine : r));
        push('Routine updated.');
      } else {
        const res = await apiFetch<{ routine: AttackRoutine }>(
          `/characters/${characterId}/combat/routines`,
          { method: 'POST', body: JSON.stringify(data) },
        );
        setRoutines((p) => [...p, res.routine]);
        push('Routine created.');
      }
      setBuildMode(null);
    } catch (e: any) { push(e.message ?? 'Failed to save routine', false); }
    finally { setSaving(false); }
  }

  async function handleDelete(routineId: string) {
    setDeleting(routineId);
    try {
      await apiFetch(`/characters/${characterId}/combat/routines/${routineId}`, { method: 'DELETE' });
      setRoutines((p) => p.filter((r) => r.routineId !== routineId));
      if (executing?.routineId === routineId) setExecuting(null);
      push('Routine deleted.');
    } catch (e: any) { push(e.message ?? 'Failed to delete routine', false); }
    finally { setDeleting(null); setDeleteId(null); }
  }

  async function handleExport(routine: AttackRoutine) {
    try {
      const data = await apiFetch<object>(
        `/characters/${characterId}/combat/routines/${routine.routineId}/export`,
      );
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${routine.name.replace(/[^a-z0-9]+/gi, '_')}.darkfeld-routine`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) { push(e.message ?? 'Export failed', false); }
  }

  async function handleDuplicate(routine: AttackRoutine) {
    setDuplicating(routine.routineId);
    try {
      const res = await apiFetch<{ routine: AttackRoutine }>(
        `/characters/${characterId}/combat/routines/${routine.routineId}/duplicate`,
        { method: 'POST' },
      );
      setRoutines((p) => [...p, res.routine]);
      push(`"${res.routine.name}" created.`);
    } catch (e: any) { push(e.message ?? 'Failed to duplicate routine', false); }
    finally { setDuplicating(null); }
  }

  async function handleImportFile(file: File) {
    try {
      const json = JSON.parse(await file.text());
      const res  = await apiFetch<{ routine: AttackRoutine }>(
        `/characters/${characterId}/combat/routines/import`,
        { method: 'POST', body: JSON.stringify(json) },
      );
      setRoutines((p) => [...p, res.routine]);
      push(`"${res.routine.name}" imported.`);
    } catch (e: any) { push(e.message ?? 'Import failed — check the file is a valid .darkfeld-routine', false); }
  }

  async function handleShareToLibrary(routine: AttackRoutine) {
    if (!worldId) return;
    setSharing(routine.routineId);
    try {
      const res = await apiFetch<{ routine: LibraryRoutine }>(
        `/worlds/${worldId}/combat/routines`,
        { method: 'POST', body: JSON.stringify({
          name: routine.name, gameSystem: routine.gameSystem, definition: routine.definition,
        })},
      );
      setLibrary((p) => [...p, res.routine]);
      push(`"${routine.name}" added to the world library.`);
    } catch (e: any) { push(e.message ?? 'Failed to share routine', false); }
    finally { setSharing(null); }
  }

  // ── Library CRUD ──────────────────────────────────────────────────────────
  async function handleLibSave(data: { name: string; gameSystem: RuleSystem; definition: RoutineNode[] }) {
    if (!worldId || !buildMode || typeof buildMode !== 'object' || !('editLib' in buildMode)) return;
    const editLibMode = buildMode as { editLib: LibraryRoutine };
    setLibSaving(true);
    try {
      const res = await apiFetch<{ routine: LibraryRoutine }>(
        `/worlds/${worldId}/combat/routines/${editLibMode.editLib.routineId}`,
        { method: 'PATCH', body: JSON.stringify(data) },
      );
      setLibrary((p) => p.map((r) => r.routineId === editLibMode.editLib.routineId ? res.routine : r));
      setBuildMode(null);
      push('Library routine updated.');
    } catch (e: any) { push(e.message ?? 'Failed to save library routine', false); }
    finally { setLibSaving(false); }
  }

  async function handleLibDelete(routineId: string) {
    if (!worldId) return;
    try {
      await apiFetch(`/worlds/${worldId}/combat/routines/${routineId}`, { method: 'DELETE' });
      setLibrary((p) => p.filter((r) => r.routineId !== routineId));
      push('Removed from library.');
    } catch (e: any) { push(e.message ?? 'Failed to delete', false); }
  }

  async function handleImportFromLibrary(libRoutine: LibraryRoutine) {
    setImportingLibId(libRoutine.routineId);
    try {
      const res = await apiFetch<{ routine: AttackRoutine }>(
        `/characters/${characterId}/combat/routines/import`,
        { method: 'POST', body: JSON.stringify({
          _darkfeldRoutine: true, version: 1,
          name: libRoutine.name, gameSystem: libRoutine.gameSystem, definition: libRoutine.definition,
        })},
      );
      setRoutines((p) => [...p, res.routine]);
      push(`"${libRoutine.name}" added to My Routines.`);
    } catch (e: any) { push(e.message ?? 'Import failed', false); }
    finally { setImportingLibId(null); }
  }

  async function loadHistory(cursor?: string | null) {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      if (cursor) params.append('cursor', cursor);
      const res = await apiFetch<{
        logs: RollLog[];
        hasMore: boolean;
        nextCursor?: string;
      }>(`/characters/${characterId}/combat/roll-history?${params.toString()}`);
      setHistoryLogs(cursor ? (p) => [...p, ...res.logs] : res.logs);
      setHistoryMore(res.hasMore);
      setHistoryCursor(res.nextCursor ?? null);
    } catch (e: any) {
      console.warn('Failed to load roll history:', e);
    } finally {
      setHistoryLoading(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center gap-2 text-parchment-muted/50 py-8 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );
  if (error) return <div className="card border-danger/30 text-center py-8"><p className="text-danger text-sm">{error}</p></div>;

  // ── Builder modes ────────────────────────────────────────────────────────
  if (buildMode !== null && (buildMode === 'create' || (typeof buildMode === 'object' && ('edit' in buildMode || 'editLib' in buildMode)))) {
    const bm        = buildMode!;
    const isEditLib = typeof bm === 'object' && 'editLib' in bm;
    const isEdit    = typeof bm === 'object' && 'edit'    in bm;
    const initial   = isEdit ? (bm as { edit: AttackRoutine }).edit
                    : isEditLib ? (bm as { editLib: LibraryRoutine }).editLib as Partial<AttackRoutine>
                    : undefined;
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setBuildMode(null)}
            className="p-1 text-parchment-muted/60 hover:text-parchment rounded transition-colors">
            <ChevronDown className="w-4 h-4 -rotate-90" />
          </button>
          <h3 className="font-display text-sm text-gold">
            {isEditLib ? 'Edit Library Routine' : isEdit ? 'Edit Routine' : 'New Routine'}
          </h3>
        </div>
        <RoutineBuilder
          initial={initial}
          defaultGameSystem={gameSystem}
          saving={isEditLib ? libSaving : saving}
          onSave={isEditLib ? handleLibSave : handleSave}
          onCancel={() => setBuildMode(null)}
        />
      </div>
    );
  }

  const filteredLib = library.filter((r) => {
    if (!libSearch.trim()) return true;
    const q = libSearch.toLowerCase();
    return r.name.toLowerCase().includes(q)
      || r.description?.toLowerCase().includes(q)
      || r.tags?.some((t) => t.includes(q));
  });

  const switchCharacters = campaignId ? campaignCharacters : myChars;
  const switchReturnState = campaignId
    ? { returnTo: `/campaigns/${campaignId}?tab=characters`, returnLabel: 'Back to campaign' }
    : worldId
      ? { returnTo: `/worlds/${worldId}`, returnLabel: 'Back to world' }
      : { returnTo: '/characters', returnLabel: 'Back to characters' };

  function switchCampaign(nextCampaignId?: string) {
    if (nextCampaignId === campaignId || (!nextCampaignId && !campaignId)) return;
    const recent = readRecentCombatContext(effectiveUserId);
    const characters = nextCampaignId
      ? combatContext?.campaigns.find((campaign) => campaign.campaignId === nextCampaignId)?.characters ?? []
      : combatContext?.unassignedCharacters ?? [];
    const rememberedId = rememberedCharacterForScope(recent, nextCampaignId);
    const nextCharacter = characters.find((candidate) => candidate.characterId === rememberedId) ?? characters[0];
    if (!nextCharacter) return;
    saveRecentCombatContext(effectiveUserId, nextCharacter.characterId, nextCampaignId);
    navigate(`/characters/${nextCharacter.characterId}?tab=routines`, {
      state: nextCampaignId
        ? { returnTo: `/campaigns/${nextCampaignId}?tab=characters`, returnLabel: 'Back to campaign' }
        : { returnTo: '/characters', returnLabel: 'Back to characters' },
    });
  }

  return (
    <div className="space-y-4">

      {/* ── Toasts ── */}
      <ToastList toasts={toasts} onDismiss={dismiss} />

      {/* ── Campaign and character quick-switch ───────────────────────────── */}
      {!hideCharSwitcher && (combatContext || switchCharacters.length > 0) && (
        <div className="space-y-2 pb-3 border-b border-darkbg-border/50">
          {combatContext && (combatContext.campaigns.length > 0 || combatContext.unassignedCharacters.length > 0) && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="w-[4.75rem] text-[10px] text-parchment-muted/40 uppercase tracking-widest shrink-0 mr-0.5">
                Campaign:
              </span>
              {combatContext.campaigns.map((campaign) => (
                <button
                  key={campaign.campaignId}
                  type="button"
                  onClick={() => switchCampaign(campaign.campaignId)}
                  className={`px-2.5 py-0.5 rounded-full text-xs border transition-colors ${
                    campaign.campaignId === campaignId
                      ? 'bg-gold/15 border-gold/50 text-gold cursor-default'
                      : 'border-darkbg-border text-parchment-muted/60 hover:border-gold/30 hover:text-parchment'
                  }`}
                >
                  {campaign.name}
                </button>
              ))}
              {combatContext.unassignedCharacters.length > 0 && (
                <button
                  type="button"
                  onClick={() => switchCampaign(undefined)}
                  className={`px-2.5 py-0.5 rounded-full text-xs border transition-colors ${
                    !campaignId
                      ? 'bg-gold/15 border-gold/50 text-gold cursor-default'
                      : 'border-darkbg-border text-parchment-muted/60 hover:border-gold/30 hover:text-parchment'
                  }`}
                >
                  Unassigned
                </button>
              )}
            </div>
          )}
          {switchCharacters.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="w-[4.75rem] text-[10px] text-parchment-muted/40 uppercase tracking-widest shrink-0 mr-0.5">
                Character:
              </span>
              {switchCharacters.map((c) => (
                <button
                  key={c.characterId}
                  onClick={() => navigate(`/characters/${c.characterId}?tab=routines`, { state: switchReturnState })}
                  className={`px-2.5 py-0.5 rounded-full text-xs border transition-colors ${
                    c.characterId === characterId
                      ? 'bg-gold/15 border-gold/50 text-gold cursor-default'
                      : 'border-darkbg-border text-parchment-muted/60 hover:border-gold/30 hover:text-parchment'
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Discord combat channel notice (DM/admin only, when not configured) ── */}
      {campaignId && canManageCombatScope && combatChannelId === null && (
        <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg
                        bg-amber-500/10 border border-amber-500/20 text-amber-300/80 text-xs">
          <Zap className="w-3.5 h-3.5 shrink-0 text-amber-400/70" />
          <span className="flex-1">
            Discord combat channel not configured — roll results won't auto-post to Discord.
          </span>
          <Link
            to={`/campaigns/${campaignId}`}
            className="shrink-0 text-gold hover:text-gold/80 underline underline-offset-2 transition-colors whitespace-nowrap"
          >
            Configure →
          </Link>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          1. Campaign Modifiers
          ═══════════════════════════════════════════════════════════════ */}
      {campaignId && (
        <section className="bg-darkbg-card border border-darkbg-border rounded-lg overflow-hidden">
          {/* Section header — click anywhere on the row to expand/collapse */}
          <button
            type="button"
            onClick={() => setModOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-4 py-3 text-left
                       hover:bg-darkbg-tertiary transition-colors"
          >
            <Zap className="w-3.5 h-3.5 text-gold/60 shrink-0" />
            <span className="font-display text-sm text-parchment flex-1">Combat Modifiers</span>
            <span className="text-[10px] text-parchment-muted/40 tabular-nums mr-1">
              {modifiers.length} modifier{modifiers.length !== 1 ? 's' : ''}
            </span>
            {modOpen ? <ChevronUp className="w-4 h-4 text-parchment-muted/30" />
                     : <ChevronDown className="w-4 h-4 text-parchment-muted/30" />}
          </button>

          {modOpen && (
            <div className="border-t border-darkbg-border/60 px-4 py-3 space-y-4">
              {modifiers.length > 0 && (
                <div className="bg-darkbg-tertiary/50 border border-darkbg-border/50 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-semibold text-parchment-muted/70 uppercase tracking-wide">Active Now</p>
                  <div className="grid grid-cols-2 gap-2">
                    {modifiers.filter((m) => m.isActive).slice(0, 6).map((m) => (
                      <div key={m.modifierId} className="flex items-start gap-2 text-xs">
                        <div className="pt-0.5 text-parchment-muted/40">
                          {m.effects?.some((e) => e.rollModifier === 'advantage' || (e.value ?? 0) > 0) ? '▲' : '▼'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-display text-parchment-muted/80 truncate">{m.name}</p>
                          <p className="text-parchment-muted/50 text-[9px]">
                            {m.effects?.length ?? 0} effect{m.effects?.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {modifiers.filter((m) => m.isActive).length > 6 && (
                    <p className="text-[9px] text-parchment-muted/40 text-center">
                      +{modifiers.filter((m) => m.isActive).length - 6} more
                    </p>
                  )}
                </div>
              )}
              <ModifierPanel
                campaignId={campaignId}
                characters={campaignCharacters}
                isOwnerOrAdmin={canManageCombatScope}
                currentUserId={effectiveUserId}
                defaultOwnerType={canManageCombatScope ? 'world' : 'character'}
                ownerCharacterId={char?.characterId}
                ownerCharacterName={char?.name}
                compact
                onModifiersChange={setModifiers}
              />
            </div>
          )}
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          2. Active Execution (inline — tab stays visible)
          ═══════════════════════════════════════════════════════════════ */}
      {executing && (
        <section ref={executorRef}
          className="bg-darkbg-card border-2 border-gold/30 rounded-lg overflow-hidden
                     shadow-[0_0_20px_rgba(200,170,100,0.08)]">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-gold/5 border-b border-gold/20">
            <Swords className="w-3.5 h-3.5 text-gold/70 shrink-0 animate-pulse" />
            <span className="font-display text-sm text-gold/90 flex-1 truncate">
              {executing.name}
            </span>
            <button onClick={() => setExecuting(null)}
              className="p-1 text-parchment-muted/40 hover:text-parchment transition-colors shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4">
            <RoutineExecutor
              routine={executing}
              characterId={characterId}
              charName={charName}
              char={char}
              modifiers={modifiers}
              outputTargets={outputTargets}
              onClose={() => setExecuting(null)}
            />
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          3. My Routines
          ═══════════════════════════════════════════════════════════════ */}
      <section className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-display text-sm text-parchment">
            My Routines
            <span className="ml-2 text-[10px] text-parchment-muted/40 font-sans font-normal">
              {routines.length > 0 ? `${routines.length} saved` : ''}
            </span>
          </h3>
          <div className="flex items-center gap-2 shrink-0">
            <label className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-darkbg-border rounded
                               cursor-pointer text-parchment-muted/60 hover:text-parchment hover:border-parchment-muted/40
                               transition-colors">
              <Upload className="w-3.5 h-3.5" /> Import
              <input ref={fileInputRef} type="file" accept=".darkfeld-routine,application/json"
                className="sr-only"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFile(f); if (fileInputRef.current) fileInputRef.current.value = ''; }} />
            </label>
            <button onClick={() => setBuildMode('create')}
              className="btn-primary flex items-center gap-1.5 text-xs">
              <Plus className="w-3.5 h-3.5" /> New
            </button>
          </div>
        </div>

        {/* ── Output target selector (only when there are routines to roll) ── */}
        {routines.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-parchment-muted/40 uppercase tracking-widest shrink-0">
              Send rolls to
            </span>
            <div className="flex items-center gap-0.5 bg-darkbg-tertiary rounded-lg p-0.5">
              <button
                onClick={() => toggleOutputTarget('dndbeyond')}
                aria-pressed={outputTargets.includes('dndbeyond')}
                title="Send rolls to D&D Beyond via the browser extension"
                className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs transition-colors ${
                  outputTargets.includes('dndbeyond')
                    ? 'bg-gold/20 border border-gold/60 text-gold-light font-medium shadow-sm'
                    : 'text-parchment-muted/40 hover:text-parchment-muted'
                }`}
              >
                <Sword className="w-3 h-3" />
                D&amp;D Beyond
              </button>
              <button
                onClick={() => toggleOutputTarget('roll20')}
                aria-pressed={outputTargets.includes('roll20')}
                title="Send rolls to Roll20 via the browser extension"
                className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs transition-colors ${
                  outputTargets.includes('roll20')
                    ? 'bg-gold/20 border border-gold/60 text-gold-light font-medium shadow-sm'
                    : 'text-parchment-muted/40 hover:text-parchment-muted'
                }`}
              >
                <Dices className="w-3 h-3" />
                Roll20
              </button>
              <button
                onClick={() => toggleOutputTarget('talespire')}
                aria-pressed={outputTargets.includes('talespire')}
                title="Publish trusted Darkfeld results to everyone on the TaleSpire board"
                className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs transition-colors ${
                  outputTargets.includes('talespire')
                    ? 'bg-gold/20 border border-gold/60 text-gold-light font-medium shadow-sm'
                    : 'text-parchment-muted/40 hover:text-parchment-muted'
                }`}
              >
                <Puzzle className="w-3 h-3" />
                TaleSpire
              </button>
              <button
                onClick={() => toggleOutputTarget('discord')}
                aria-pressed={outputTargets.includes('discord')}
                title="No VTT overlay — results post directly to the campaign's Discord channel"
                className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs transition-colors ${
                  outputTargets.includes('discord')
                    ? 'bg-gold/20 border border-gold/60 text-gold-light font-medium shadow-sm'
                    : 'text-parchment-muted/40 hover:text-parchment-muted'
                }`}
              >
                <Hash className="w-3 h-3" />
                Discord
              </button>
              <button
                onClick={() => {
                  setOutputTargetsState([]);
                  if (!isPreview) setStoredOutputTargets(characterId, []);
                }}
                aria-pressed={outputTargets.length === 0}
                title="Copy-paste only — no rolls sent to the extension"
                className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs transition-colors ${
                  outputTargets.length === 0
                    ? 'bg-gold/20 border border-gold/60 text-gold-light font-medium shadow-sm'
                    : 'text-parchment-muted/40 hover:text-parchment-muted'
                }`}
              >
                <Copy className="w-3 h-3" />
                Copy Only
              </button>
            </div>
          </div>
        )}

        {routines.length === 0 ? (
          <div className="card text-center py-10 space-y-2">
            <Swords className="w-7 h-7 mx-auto text-parchment-muted/20" />
            <p className="text-parchment-muted/60 text-sm">No routines yet.</p>
            {worldId && library.length > 0 && (
              <p className="text-parchment-muted/40 text-xs">Browse the World Library below to import ready-made routines.</p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {routines.map((r, i) => {
              const isExec      = executing?.routineId === r.routineId;
              const isConfirm   = deleteId === r.routineId;
              const isDeleting  = deleting === r.routineId;
              const isDragging  = dragIndex === i;
              const isDragOver  = dragOverIndex === i && dragIndex !== i;
              return (
                <div key={r.routineId}
                  draggable
                  onDragStart={() => handleDragStart(i)}
                  onDragOver={(e) => handleDragOver(e, i)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, i)}
                  onDragEnd={handleDragEnd}
                  className={`bg-darkbg-card border rounded-lg transition-all duration-150 overflow-hidden ${
                    isDragging
                      ? 'opacity-40 border-gold/40'
                      : isDragOver
                        ? 'border-gold/60 shadow-[0_0_0_2px_rgba(200,170,100,0.25)] -translate-y-0.5'
                        : isExec
                          ? 'border-gold/40 shadow-[0_0_12px_rgba(200,170,100,0.10)]'
                          : 'border-darkbg-border hover:border-gold/30 hover:bg-darkbg-card/80'
                  }`}>
                  <div className="flex items-center gap-0 min-w-0">
                    {/* Drag handle */}
                    <div
                      className="px-2 py-3.5 text-parchment-muted/20 hover:text-parchment-muted/50
                                 cursor-grab active:cursor-grabbing shrink-0 transition-colors"
                      title="Drag to reorder"
                    >
                      <GripVertical className="w-3.5 h-3.5" />
                    </div>

                    {/* Primary CTA — Roll */}
                    {!isConfirm && (
                      <button
                        onClick={() => setExecuting(isExec ? null : r)}
                        title={isExec ? 'Close executor' : 'Roll this routine'}
                        className={`flex items-center gap-1.5 px-3 py-3.5 shrink-0 font-display text-sm
                                    border-r border-darkbg-border/60 transition-colors ${
                          isExec
                            ? 'text-gold bg-gold/10 hover:bg-gold/15'
                            : 'text-parchment-muted/60 hover:text-success hover:bg-success/10'
                        }`}
                      >
                        {isExec ? <X className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        <span className="hidden sm:inline">{isExec ? 'Close' : 'Roll'}</span>
                      </button>
                    )}

                    {/* Name + metadata */}
                    <div className="flex-1 min-w-0 px-3 py-3">
                      <p className="font-display text-sm text-parchment truncate">{r.name}</p>
                      <p className="text-xs text-parchment-muted/50">
                        {SYS[r.gameSystem] ?? r.gameSystem} · {r.definition.length} attack{r.definition.length !== 1 ? 's' : ''}
                      </p>
                    </div>

                    {/* Secondary actions */}
                    {!isConfirm ? (
                      <div className="flex items-center gap-0.5 px-2 shrink-0">
                        {worldId && canManageCombatScope && (
                          <button onClick={() => handleShareToLibrary(r)} title="Share to world library"
                            disabled={sharing === r.routineId}
                            className="p-1.5 rounded text-parchment-muted/30 hover:text-indigo-400 hover:bg-indigo-400/10 transition-colors disabled:opacity-30">
                            {sharing === r.routineId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                          </button>
                        )}
                        <button onClick={() => handleExport(r)} title="Export as file"
                          className="p-1.5 rounded text-parchment-muted/30 hover:text-parchment hover:bg-darkbg-tertiary transition-colors">
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDuplicate(r)} disabled={duplicating === r.routineId} title="Duplicate"
                          className="p-1.5 rounded text-parchment-muted/30 hover:text-parchment hover:bg-darkbg-tertiary transition-colors disabled:opacity-50">
                          {duplicating === r.routineId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => setBuildMode({ edit: r })} title="Edit"
                          className="p-1.5 rounded text-parchment-muted/30 hover:text-gold hover:bg-gold/10 transition-colors">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setDeleteId(r.routineId)} title="Delete"
                          className="p-1.5 rounded text-parchment-muted/30 hover:text-danger hover:bg-danger/10 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-3 border-l border-danger/30">
                        <span className="text-xs text-parchment-muted whitespace-nowrap">Delete?</span>
                        <button onClick={() => setDeleteId(null)} disabled={isDeleting}
                          className="px-2 py-0.5 text-xs text-parchment-muted border border-darkbg-border rounded hover:border-parchment-muted/50 transition-colors disabled:opacity-50">
                          No
                        </button>
                        <button onClick={() => handleDelete(r.routineId)} disabled={isDeleting}
                          className="px-2 py-0.5 text-xs text-white bg-danger/80 hover:bg-danger rounded transition-colors disabled:opacity-60">
                          {isDeleting ? '…' : 'Yes'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          4. World Library (collapsed by default)
          ═══════════════════════════════════════════════════════════════ */}
      {worldId && (
        <section className="bg-darkbg-card border border-darkbg-border rounded-lg overflow-hidden">
          <button onClick={() => setLibOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-darkbg-tertiary/50 transition-colors">
            <Library className="w-3.5 h-3.5 text-parchment-muted/40 shrink-0" />
            <span className="font-display text-sm text-parchment flex-1">World Library</span>
            <span className="text-[10px] text-parchment-muted/40 tabular-nums mr-1">
              {libLoading ? '…' : `${library.length} routine${library.length !== 1 ? 's' : ''}`}
            </span>
            {libOpen ? <ChevronUp className="w-4 h-4 text-parchment-muted/30" />
                     : <ChevronDown className="w-4 h-4 text-parchment-muted/30" />}
          </button>

          {libOpen && (
            <div className="border-t border-darkbg-border/60 p-4 space-y-3">
              {libLoading ? (
                <div className="flex items-center gap-2 text-parchment-muted/50 justify-center py-4 text-xs">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading library…
                </div>
              ) : library.length === 0 ? (
                <div className="text-center py-6 space-y-2">
                  <BookOpen className="w-6 h-6 mx-auto text-parchment-muted/20" />
                  <p className="text-parchment-muted/50 text-xs">No routines in the world library yet.</p>
                  {canManageCombatScope && (
                    <p className="text-parchment-muted/30 text-xs">
                      Use the <Share2 className="inline w-3 h-3 mx-0.5" /> button on a My Routine to publish it here.
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-parchment-muted/30 pointer-events-none" />
                    <input value={libSearch} onChange={(e) => setLibSearch(e.target.value)}
                      placeholder="Search by name, system or tag…"
                      className="w-full bg-darkbg border border-darkbg-border rounded pl-9 pr-3 py-2 text-parchment text-xs
                                 placeholder-parchment-muted/25 focus:outline-none focus:border-gold" />
                  </div>
                  {filteredLib.length === 0 ? (
                    <p className="text-parchment-muted/40 text-xs text-center py-3">No matches for "{libSearch}"</p>
                  ) : (
                    <div className="space-y-2">
                      {filteredLib.map((r) => (
                        <LibraryCard
                          key={r.routineId}
                          r={r}
                          isDM={canManageCombatScope}
                          importing={importingLibId === r.routineId}
                          onImport={() => handleImportFromLibrary(r)}
                          onEdit={() => setBuildMode({ editLib: r })}
                          onDelete={() => handleLibDelete(r.routineId)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          5. Roll History (collapsed by default)
          ═══════════════════════════════════════════════════════════════ */}
      <section className="bg-darkbg-card border border-darkbg-border rounded-lg overflow-hidden">
        <button onClick={() => setHistoryOpen((v) => !v)}
          className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-darkbg-tertiary/50 transition-colors">
          <Clock className="w-3.5 h-3.5 text-parchment-muted/40 shrink-0" />
          <span className="font-display text-sm text-parchment flex-1">Roll History</span>
          <span className="text-[10px] text-parchment-muted/40 tabular-nums mr-1">
            {historyLoading ? '…' : `${historyLogs.length} roll${historyLogs.length !== 1 ? 's' : ''}`}
          </span>
          {historyOpen ? <ChevronUp className="w-4 h-4 text-parchment-muted/30" />
                       : <ChevronDown className="w-4 h-4 text-parchment-muted/30" />}
        </button>

        {historyOpen && (
          <div className="border-t border-darkbg-border/60 p-4 space-y-3">
            {historyLoading && historyLogs.length === 0 ? (
              <div className="flex items-center gap-2 text-parchment-muted/50 justify-center py-4 text-xs">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading history…
              </div>
            ) : historyLogs.length === 0 ? (
              <div className="text-center py-6 space-y-2">
                <Clock className="w-6 h-6 mx-auto text-parchment-muted/20" />
                <p className="text-parchment-muted/50 text-xs">No rolls recorded yet.</p>
                <p className="text-parchment-muted/30 text-xs">
                  Execute a routine above to start building your roll history.
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {historyLogs.map((log) => (
                    <RollHistoryCard key={log.logId} log={log} />
                  ))}
                </div>
                {historyMore && (
                  <div className="flex justify-center pt-2">
                    <button
                      onClick={() => loadHistory(historyCursor)}
                      disabled={historyLoading}
                      className="px-3 py-1.5 text-xs border border-darkbg-border rounded text-parchment-muted/60
                                 hover:border-gold/40 hover:text-parchment transition-colors disabled:opacity-40"
                    >
                      {historyLoading ? <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> : null}
                      Load more
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          6. VTT Integrations — install guide (collapsed by default)
          ═══════════════════════════════════════════════════════════════ */}
      <section className="bg-darkbg-card border border-darkbg-border rounded-lg overflow-hidden">
        <button
          onClick={() => setPluginOpen((v) => !v)}
          className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-darkbg-tertiary/50 transition-colors"
        >
          <Puzzle className="w-3.5 h-3.5 text-parchment-muted/40 shrink-0" />
          <span className="font-display text-sm text-parchment flex-1">VTT Integrations</span>
          <span className="text-[10px] text-parchment-muted/40 mr-1">Install and connect</span>
          {pluginOpen
            ? <ChevronUp   className="w-4 h-4 text-parchment-muted/30" />
            : <ChevronDown className="w-4 h-4 text-parchment-muted/30" />}
        </button>

        {pluginOpen && (
          <div className="border-t border-darkbg-border/60 p-4 space-y-5">

            {/* Intro */}
            <div>
              <p className="font-display text-parchment text-base mb-1">Connect Darkfeld to your VTT</p>
              <p className="text-parchment-muted/60 text-xs">
                TaleSpire uses the in-app Darkfeld Combat Symbiote. Roll20 and D&amp;D Beyond use the
                browser extension. Enable every destination that should receive the roll under <strong className="text-parchment">Send rolls to</strong> above.
              </p>
            </div>

            {/* TaleSpire Symbiote */}
            <div className="space-y-2">
              <div>
                <p className="font-display text-parchment text-sm">TaleSpire — install the Symbiote</p>
                <p className="text-parchment-muted/60 text-xs">
                  Only the player sending the roll needs to install it. Results are published to everyone on the current board.
                </p>
              </div>
              <p className="text-[#a8cfe8] text-xs font-semibold">Recommended — install from TaleSpire Community Mods</p>
              <ol className="space-y-1 list-decimal list-inside text-parchment-muted/80 text-xs">
                <li>In TaleSpire, open <strong className="text-parchment">Settings → Symbiotes</strong> and enable Symbiotes</li>
                <li>Open a campaign or board and click the orange <strong className="text-parchment">+</strong> in the top bar to open the Library</li>
                <li>Select <strong className="text-parchment">Community Mods</strong> (the globe icon), then the <strong className="text-parchment">Symbiotes</strong> category</li>
                <li>Choose <strong className="text-parchment">Official Repository</strong>, search for <strong className="text-parchment">Darkfeld</strong>, and install it</li>
                <li>Close the Library and open <strong className="text-parchment">Darkfeld Combat</strong> from the Symbiotes panel</li>
                <li>Click <strong className="text-parchment">Sign in with Discord</strong> and wait for <strong className="text-parchment">Connected</strong></li>
                <li>Select <strong className="text-parchment">TaleSpire</strong> above, then roll and confirm hits normally</li>
                <li>Review older results under TaleSpire's <strong className="text-parchment">Space menu → Chat/Messages</strong></li>
              </ol>
              <p className="text-parchment-muted/50 text-[10px]">
                Subscribing on the mod.io website alone does not install the package; install it through TaleSpire's Community Mods library.
              </p>
              <div className="rounded border border-darkbg-border/60 bg-darkbg/30 p-3 space-y-2">
                <p className="text-parchment text-xs font-semibold">Manual ZIP alternative</p>
                <button
                  type="button"
                  onClick={() => { void downloadTaleSpirePackage(); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-darkbg border border-[#8aafc2]/30
                             text-[#a8cfe8] hover:border-[#a8cfe8]/60 text-xs transition-colors"
                >
                  <Download className="w-3 h-3" />
                  Download TaleSpire Symbiote
                </button>
                <ol className="space-y-1 list-decimal list-inside text-parchment-muted/70 text-xs">
                  <li>Open the right-side Symbiotes panel and click <strong className="text-parchment">Open Symbiotes Directory</strong></li>
                  <li>Extract the downloaded ZIP directly into that directory</li>
                  <li>Confirm the path is <strong className="text-parchment">Symbiotes/{TALESPIRE_SYMBIOTE_FOLDER}/manifest.json</strong>, without an extra nested folder</li>
                </ol>
              </div>
              <p className="text-parchment-muted/40 text-[10px]">
                Development Mode and Create New Symbiote are not required. The package loads the hosted integration and receives updates when reloaded.{' '}
                <a href={TALESPIRE_SYMBIOTE_DOCS_URL} target="_blank" rel="noopener noreferrer" className="text-[#8aafc2] hover:text-[#a8cfe8]">
                  Official TaleSpire installation guide
                </a>
              </p>
            </div>

            <div className="pt-1 border-t border-darkbg-border/60">
              <p className="font-display text-parchment text-sm">Roll20 and D&amp;D Beyond — install the browser extension</p>
            </div>

            {/* Install */}
            <div className="space-y-1.5">
              <p className="text-parchment text-xs font-semibold uppercase tracking-wide">1. Install</p>
              <p className="text-parchment-muted/60 text-xs mb-2">Choose the official version for your browser.</p>
              <div className="flex flex-wrap gap-2">
                <a
                  href={CHROME_WEB_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-darkbg border border-darkbg-border
                             text-gold/80 hover:text-gold hover:border-gold/40 text-xs transition-colors"
                >
                  <Download className="w-3 h-3" />
                  Chrome Web Store
                </a>
                <a
                  href="/downloads/darkfeld-plugin-firefox.xpi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-darkbg border border-darkbg-border
                             text-gold/80 hover:text-gold hover:border-gold/40 text-xs transition-colors"
                >
                  <Download className="w-3 h-3" />
                  Firefox
                </a>
              </div>
            </div>

            {/* Chromium install */}
            <div className="space-y-1.5">
              <p className="text-parchment text-xs font-semibold uppercase tracking-wide">2a. Install in Chrome, Edge, or Brave</p>
              <ol className="space-y-1 list-decimal list-inside text-parchment-muted/80 text-xs">
                <li>Click <strong className="text-parchment">Chrome Web Store</strong> above</li>
                <li>Click <strong className="text-parchment">Add to Chrome</strong> (or the equivalent button shown by your browser)</li>
                <li>Approve the requested permissions; the store version updates automatically</li>
              </ol>
              <p className="text-parchment-muted/40 text-[10px] pt-0.5">
                Enhanced Safe Browsing may show <strong className="text-parchment-muted/60">Proceed with caution</strong> while
                Google establishes trust for our publisher account. Choose <strong className="text-parchment-muted/60">Continue to install</strong> to proceed.
              </p>
            </div>

            {/* Firefox install */}
            <div className="space-y-1.5">
              <p className="text-parchment text-xs font-semibold uppercase tracking-wide">2b. Install in Firefox</p>
              <ol className="space-y-1 list-decimal list-inside text-parchment-muted/80 text-xs">
                <li>Click the <strong className="text-parchment">Firefox</strong> download button above — Firefox opens an install prompt automatically</li>
                <li>Click <strong className="text-parchment">Add</strong> when prompted — leave "Allow in private windows" unchecked</li>
                <li>The extension installs permanently and updates automatically</li>
              </ol>
              <p className="text-parchment-muted/40 text-[10px] pt-0.5">
                Using Chrome to download for Firefox? Open the downloaded <strong className="text-parchment-muted/60">.xpi</strong> file
                in Firefox to install it.
              </p>
            </div>

            {/* Connect */}
            <div className="space-y-1.5">
              <p className="text-parchment text-xs font-semibold uppercase tracking-wide">3. Connect</p>
              <ol className="space-y-1 list-decimal list-inside text-parchment-muted/80 text-xs">
                <li>Open <strong className="text-parchment">Roll20</strong> or <strong className="text-parchment">D&amp;D Beyond</strong> in another tab</li>
                <li>Look for the <strong className="text-parchment">Darkfeld icon</strong> in the bottom-right corner of the page</li>
                <li>Click it and sign in with your Discord account</li>
                <li>The icon dot turns <strong className="text-parchment">green</strong> — you're connected</li>
              </ol>
            </div>

            {/* Use */}
            <div className="space-y-1.5 pb-1">
              <p className="text-parchment text-xs font-semibold uppercase tracking-wide">4. Roll</p>
              <ol className="space-y-1 list-decimal list-inside text-parchment-muted/80 text-xs">
                <li>Click <strong className="text-parchment">⬡ Roll</strong> on any routine — attack rolls appear in your VTT immediately</li>
                <li>Toggle which attacks hit or miss, then click <strong className="text-parchment">Confirm Hits</strong></li>
                <li>Damage results appear in your VTT automatically</li>
              </ol>
            </div>

          </div>
        )}
      </section>

    </div>
  );
}
